/**
 * One-time carry-over from the pre-React prototype, which kept some data only in each browser:
 * - clients and pest activity in localStorage (never shared between devices),
 * - unsent offline operations in the "pcs-offline-work" IndexedDB database.
 * Clients and activity unknown to the server are queued as normal changes; legacy queued operations are moved
 * into the new outbox in their original order. Nothing is deleted from the old stores.
 */
import { db, getMeta, tableFor } from './db';
import { isManager } from './domain';
import type { Namespace, User } from './types';
import { normalize } from './normalize';
import { putEntity } from '../sync/outbox';
import { nowIso, uid } from '../lib/format';

const DONE_KEY = (userId: string) => `legacy-import:${userId}`;

function readLocal(key: string): Array<Record<string, unknown>> {
  try { const value = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(value) ? value : []; } catch { return []; }
}

async function importList(namespace: Namespace, storageKey: string, label: (item: Record<string, unknown>) => string) {
  for (const raw of readLocal(storageKey)) {
    const id = String(raw.id ?? '').replace(/[^a-z0-9._:-]+/gi, '-').slice(0, 128);
    if (!id || await tableFor(namespace).get(id)) continue;
    await putEntity(namespace, normalize(namespace, raw, id) as never, label(raw));
  }
}

interface LegacyOperation { id: string; ownerId: string; status: string; kind: 'record' | 'binary'; namespace?: string; key?: string; value?: unknown; url?: string; method?: string; headers?: Record<string, string>; blob?: Blob; label?: string; createdAt?: string }

function readLegacyQueue(): Promise<LegacyOperation[]> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve([]);
    const request = indexedDB.open('pcs-offline-work');
    request.onupgradeneeded = () => { request.transaction?.abort(); resolve([]); };
    request.onerror = () => resolve([]);
    request.onsuccess = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains('operations')) { database.close(); return resolve([]); }
      const all = database.transaction('operations').objectStore('operations').getAll();
      all.onsuccess = () => { database.close(); resolve(all.result as LegacyOperation[]); };
      all.onerror = () => { database.close(); resolve([]); };
    };
  });
}

async function importLegacyQueue(user: User) {
  const operations = (await readLegacyQueue()).filter((item) => item.ownerId === user.id && item.status !== 'done').sort((a, b) => a.id.localeCompare(b.id));
  for (const operation of operations) {
    const createdAt = operation.createdAt || nowIso();
    if (operation.kind === 'record' && operation.namespace && operation.key) {
      const namespace = operation.namespace as Namespace;
      if (!['job-records', 'rodent-plans', 'technicians', 'clients', 'jobs', 'pest-activity'].includes(namespace)) continue;
      await putEntity(namespace, normalize(namespace, operation.value, operation.key) as never, operation.label || 'Offline record');
    } else if (operation.kind === 'binary' && operation.url && operation.blob) {
      const blobId = uid('legacy_');
      await db.transaction('rw', db.blobs, db.outbox, async () => {
        await db.blobs.put({ id: blobId, blob: operation.blob!, name: operation.label || 'Upload', type: operation.blob!.type, size: operation.blob!.size, createdAt, uploaded: false });
        await db.outbox.add({ kind: 'legacy-request', opId: uid('op_'), ownerId: user.id, entityKey: `legacy/${operation.id}`, label: operation.label || 'File upload', status: 'pending', attempts: 0, createdAt, url: operation.url!, method: operation.method || 'POST', headers: operation.headers || {}, blobId });
      });
    }
  }
}

let running: Promise<void> | null = null;
export function importLegacyBrowserData(user: User): Promise<void> {
  running ??= runImport(user).finally(() => { running = null; });
  return running;
}

async function runImport(user: User) {
  if (await db.meta.get(DONE_KEY(user.id))) return;
  // Compare against the server's data, so wait until this device has completed its first download.
  if (!(await getMeta<string>('last-synced-at', ''))) return;
  await importLegacyQueue(user);
  if (isManager(user)) await importList('clients', 'pcs-client-records', (item) => `Client ${item.name || ''} (from this device)`);
  await importList('pest-activity', 'pcs-pest-activity', (item) => `Pest activity at ${item.client || 'site'} (from this device)`);
  await db.meta.put({ key: DONE_KEY(user.id), value: nowIso() });
}
